/**
 * One-tap sign-in for development (NOT shipped).
 *
 * Testing maker-checker means signing in as two different partners repeatedly,
 * and typing `dev-partner-three-26` by hand a dozen times is how a session
 * gets spent on typos rather than on the thing being tested.
 *
 * The safety property that matters: **this must be impossible to ship.**
 *
 * `import.meta.env.DEV` is not read at runtime — Vite substitutes the literal
 * `false` into a production build, so the guard below becomes an unconditional
 * `return null` and everything after it is unreachable. The credential list is
 * declared *inside* that dead region on purpose: when it lived at module
 * scope, the bundler kept it even though nothing reachable referenced it, and
 * the passwords shipped. `DevQuickLogin.test.tsx` reads the built bundle and
 * fails if any of them reappear, because a comment promising otherwise is not
 * evidence — and that test is what caught the first version of this file.
 *
 * These are the seed's development credentials
 * (`server/prisma/seed/partners.ts`). They are already in the repository and
 * are refused outright in production: the seed throws unless every
 * `SEED_PARTNER_*` variable is supplied. So this adds no secret that was not
 * already here — it only makes the existing ones convenient.
 */

import { useState } from 'react';

interface DevPartner {
  label: string;
  phone: string;
  password: string;
}

export function DevQuickLogin({
  onPick,
}: {
  /** Signs in. Rejects like any other login, so a failure is visible here. */
  onPick: (phone: string, password: string) => Promise<void>;
}): JSX.Element | null {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  /*
   * Becomes `if (true) return null;` in a production build, making every line
   * below unreachable and removable. Nothing that follows may be hoisted to
   * module scope, or it survives the build — see the file comment.
   */
  if (!import.meta.env.DEV) return null;

  /** Mirrors DEV_PARTNERS in server/prisma/seed/partners.ts. */
  const partners: readonly DevPartner[] = [
    { label: 'One (admin)', phone: '01700000001', password: 'dev-partner-one-2026' },
    { label: 'Two', phone: '01700000002', password: 'dev-partner-two-2026' },
    // Not "…-2026": the seed shortens this one to stay inside its own rule.
    { label: 'Three', phone: '01700000003', password: 'dev-partner-three-26' },
    { label: 'Four', phone: '01700000004', password: 'dev-partner-four-2026' },
  ];

  const pick = (partner: DevPartner): void => {
    setError(null);
    setBusy(partner.phone);

    void onPick(partner.phone, partner.password)
      .catch((cause: unknown) => {
        // Usually "the database has not been seeded", which is worth saying
        // plainly rather than leaving the button looking broken.
        setError(cause instanceof Error ? cause.message : String(cause));
      })
      .finally(() => setBusy(null));
  };

  return (
    <section
      aria-label="Development quick login"
      className="mt-8 rounded-lg border border-dashed border-amber-400 bg-amber-50 p-3"
    >
      <p className="mb-2 text-xs font-medium text-amber-900">
        Development only — seeded partners. This block is absent from production builds.
      </p>

      <div className="grid grid-cols-2 gap-2">
        {partners.map((partner) => (
          <button
            key={partner.phone}
            type="button"
            onClick={() => pick(partner)}
            disabled={busy !== null}
            className="min-h-touch rounded-lg bg-white px-2 py-2 text-left text-xs text-gray-800 shadow-sm disabled:opacity-60"
          >
            <span className="block font-medium">{partner.label}</span>
            <span className="block tabular-nums text-gray-500">{partner.phone}</span>
          </button>
        ))}
      </div>

      {error !== null && (
        <p role="alert" className="mt-2 text-xs text-red-800">
          {error}
        </p>
      )}
    </section>
  );
}
